import { useEffect, useState } from "react";
import { CheckCircle2, KeyRound, Loader2, Trash2, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "flex-1 bg-navy-950/60 border border-gold-500/12 rounded-lg px-3 py-2 font-ui text-sm text-slate-100 outline-none focus:border-gold-400/60";

export const PipelineKey = () => {
  const [st, setSt] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState("");
  const [test, setTest] = useState(null);

  const load = () => api.v2KeyStatus().then(setSt).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const run = async (name, fn) => {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const save = () => run("save", async () => {
    const r = await api.v2KeySave(key);
    setKey(""); setSt(r); setTest(null); toast.success("Key saved");
  });
  const remove = () => run("delete", async () => {
    const r = await api.v2KeyDelete();
    setSt(r); setTest(null); toast.success("Key removed");
  });
  const check = () => run("test", async () => {
    const r = await api.v2KeyTest();
    setTest(r.ok);
  });

  if (!st) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>;
  return (
    <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-5 max-w-2xl" data-testid="pipeline-key-panel">
      <h3 className="font-ui text-sm font-semibold text-slate-200 mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4 text-slate-300" /> Anthropic API key</h3>
      <p className="font-read text-sm text-slate-400 mb-4">Used only by Pipeline V2 (Claude writer). Stored encrypted on the server and never shown again.</p>
      <p data-testid="pipeline-key-status" className="font-ui text-sm text-slate-300 mb-4">
        {st.configured ? <>Saved key ending in <span className="font-mono2 text-gold-200">…{st.last4}</span></> : "No key saved"}
      </p>
      <div className="flex gap-2 mb-4">
        <input data-testid="pipeline-key-input" type="password" autoComplete="off" placeholder={st.configured ? "Replace key" : "sk-ant-…"}
          value={key} onChange={(e) => setKey(e.target.value)} className={inputCls} />
        <button data-testid="pipeline-key-save" onClick={save} disabled={!key.trim() || !!busy}
          className="klyr-btn-metal disabled:opacity-50 font-ui text-sm font-semibold rounded-lg px-4 py-2 transition-[filter]">
          {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </button>
      </div>
      {st.configured && (
        <div className="flex items-center gap-3">
          <button data-testid="pipeline-key-test" onClick={check} disabled={!!busy}
            className="font-ui text-sm rounded-lg px-4 py-2 border border-gold-500/30 text-slate-200 hover:border-gold-400/60 transition-colors disabled:opacity-50">
            {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Test key"}
          </button>
          {test !== null && (
            <span data-testid="pipeline-key-test-result" className={`flex items-center gap-1 font-ui text-sm ${test ? "text-emerald-300" : "text-red-300"}`}>
              {test ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {test ? "Pass" : "Fail"}
            </span>
          )}
          <button data-testid="pipeline-key-delete" onClick={remove} disabled={!!busy}
            className="ml-auto p-2 rounded-lg border border-gold-500/12 text-slate-500 hover:border-red-500/50 hover:text-red-300 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
